import { useTranslation } from 'react-i18next'
import clsx from 'clsx'
import { Badge } from '@quebon/ui/components/badge'
import { Button } from '@quebon/ui/components/button'
import type { ManualGradeCredit } from '@/types/coaching'
import { AdultQuestionView } from '../component-details/parts/AdultQuestionView'
import { AnswerEditButton } from '../component-details/parts/AnswerEditButton'
import { PartialCreditGradeToggle } from '../component-details/parts/PartialCreditGradeToggle'
import { SolutionPreviewToggle } from '../component-details/parts/SolutionPreviewToggle'
import { GradingCorrectAnswer } from './GradingCorrectAnswer'
import type { GradingSheetRow as RowModel } from './gradingRows'

export type RowSaveState = 'idle' | 'saving' | 'saved' | 'failed'

interface Props {
  row: RowModel
  presentation?: 'default' | 'plain'
  /** 화면 표시값(draft 우선) */
  shown: ManualGradeCredit | null
  changed: boolean
  saveState: RowSaveState
  readOnly: boolean
  disabled: boolean
  previewOpen: boolean
  /** 정답 문자열 노출 여부(기본 표시) */
  showAnswer?: boolean
  hidePreviewSolveDescription?: boolean
  onToggle: (grade: ManualGradeCredit) => void
  onTogglePreview: () => void
  onRequestCoaching?: (quizId: string) => void
}

const STATUS_CLASS: Record<RowModel['status'], string> = {
  unanswered: 'bg-gray-100 text-gray-600',
  pending: 'bg-amber-50 text-amber-700',
  correct: 'bg-lc-mint-100 text-lc-mint-900',
  partial: 'bg-amber-50 text-amber-700',
  incorrect: 'bg-red-50 text-red-700',
}

/**
 * 채점표 행 — 번호 · 정답 · 현재 상태(응답 여부) · O/(△)/X 토글 · [답안 편집] · [해설 미리보기].
 * 정답 문자열은 showAnswer=true일 때만 노출한다(기본 표시).
 * 저장은 상위 GradingSheet가 순차로 처리하고 행은 상태만 표시한다.
 */
export function GradingSheetRow({
  row,
  presentation = 'default',
  shown,
  changed,
  saveState,
  readOnly,
  disabled,
  previewOpen,
  showAnswer = true,
  hidePreviewSolveDescription = false,
  onToggle,
  onTogglePreview,
  onRequestCoaching,
}: Props) {
  const { t } = useTranslation('coaching')
  const label = row.label || t('gradingMode.rowLabel', { no: row.no })
  const graded = row.status === 'correct' || row.status === 'partial' || row.status === 'incorrect'
  const previewId = `grading-sheet-preview-${row.key}`

  if (presentation === 'plain') {
    // Same answer box, grade toggle and buttons as the existing sheet, flattened into the workspace; changed rows share the panels' unsaved tint.
    return <li data-testid={`grading-row-${row.key}`} data-changed={changed ? 'true' : undefined}
      className={clsx('flex min-w-0 flex-col gap-3 border-b border-border py-4', changed && '-mx-2 bg-amber-50/70 px-2 shadow-[inset_3px_0_0_var(--color-amber-400)]', saveState === 'failed' && 'bg-red-50/60')}>
      <div className="grid min-w-0 gap-3 md:grid-cols-[minmax(0,1fr)_auto] md:items-center">
        <div className="flex min-w-0 flex-col gap-2">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-medium tabular-nums">{label}</span>
            <Badge variant="secondary" data-testid="grading-row-status">{t(`gradingMode.status.${row.status}`)}</Badge>
            {row.hasStudentAnswer ? <span className="text-xs text-muted-foreground">{t('gradingMode.studentAnswered')}</span> : graded ? <span className="text-xs text-muted-foreground">{t('gradingMode.teacherGradedNoAnswer')}</span> : null}
            {saveState === 'failed' ? <span role="alert" className="text-xs text-destructive">{t('gradingMode.rowFailed')}</span> : saveState === 'saving' || saveState === 'saved' ? <span role="status" className="text-xs text-muted-foreground">{t(saveState === 'saving' ? 'gradingMode.rowSaving' : 'gradingMode.rowSaved')}</span> : null}
          </div>
          {showAnswer ? <dl className="grid min-w-0 grid-cols-[auto_minmax(0,1fr)] items-baseline gap-x-3 rounded-md bg-muted/60 px-3 py-2.5"><dt className="text-xs font-medium text-muted-foreground">{t('gradingMode.correctAnswer')}</dt>
            <dd className="m-0 min-w-0">
              {row.correctAnswerText ? <div className="overflow-x-auto break-words text-lg font-bold leading-relaxed text-red-600" data-testid="grading-row-answer"><GradingCorrectAnswer correctAnswer={row.correctAnswer} questionSnapshot={row.questionSnapshot} /></div>
                : <span className="text-sm text-muted-foreground" data-testid="grading-row-no-answer">{t('gradingMode.noCorrectAnswer')}</span>}
            </dd></dl> : null}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="w-full max-w-[260px] md:w-[220px]">
            <PartialCreditGradeToggle value={shown} onChange={onToggle} disabled={readOnly || disabled} allowPartial={row.allowPartial} ariaLabel={t('gradingMode.toggleAriaLabel', { label })} />
          </div>
          {!readOnly && onRequestCoaching ? <Button type="button" variant="outline" className="min-h-11" data-testid="edit-answer-btn" disabled={disabled} onClick={() => onRequestCoaching(row.key)}>{t('override.editAnswer')}</Button> : null}
          <Button type="button" variant="outline" className="min-h-11" data-testid="solution-preview-toggle" aria-expanded={previewOpen} aria-controls={previewId} onClick={onTogglePreview}>
            {t(previewOpen ? 'solutionPreview.returnToSolve' : 'solutionPreview.show')}
          </Button>
        </div>
      </div>
      {previewOpen ? <div id={previewId} className="min-w-0"><AdultQuestionView key={`grading-preview-${row.key}`} mode="preview" quizId={row.key} order={row.no - 1}
        questionSnapshot={row.questionSnapshot} correctAnswer={row.correctAnswer} explanation={row.explanation ?? undefined} hideSolveDescription={hidePreviewSolveDescription} /></div> : null}
    </li>
  }

  return (
    <li
      data-testid={`grading-row-${row.key}`}
      data-changed={changed ? 'true' : undefined}
      className={clsx(
        'space-y-3 px-4 py-4',
        changed && 'bg-lc-mint-100/40',
        saveState === 'failed' && 'bg-red-50/60',
      )}
    >
      <div className="grid grid-cols-1 gap-3 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-center">
        <div className="min-w-0 space-y-2.5">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-semibold tabular-nums text-foreground">{label}</span>
            <span
              data-testid="grading-row-status"
              className={clsx('inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold', STATUS_CLASS[row.status])}
            >
              {t(`gradingMode.status.${row.status}`)}
            </span>
            {row.hasStudentAnswer ? (
              <span className="inline-flex items-center rounded-full bg-sky-50 px-2 py-0.5 text-[11px] font-semibold text-sky-700">
                {t('gradingMode.studentAnswered')}
              </span>
            ) : graded ? (
              <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-semibold text-gray-600">
                {t('gradingMode.teacherGradedNoAnswer')}
              </span>
            ) : null}
            {saveState === 'saving' ? (
              <span role="status" className="text-[11px] font-semibold text-gray-500">{t('gradingMode.rowSaving')}</span>
            ) : saveState === 'saved' ? (
              <span role="status" className="text-[11px] font-semibold text-lc-mint-900">{t('gradingMode.rowSaved')}</span>
            ) : saveState === 'failed' ? (
              <span role="alert" className="text-[11px] font-semibold text-red-700">{t('gradingMode.rowFailed')}</span>
            ) : null}
          </div>
          {showAnswer ? (
            <dl className="grid grid-cols-[auto_minmax(0,1fr)] items-baseline gap-x-3 rounded-md bg-muted/60 px-3 py-2.5">
              <dt className="text-xs font-medium text-muted-foreground">
                {t('gradingMode.correctAnswer')}
              </dt>
              <dd className="m-0 min-w-0">
                {row.correctAnswerText ? (
                  <div
                    className="overflow-x-auto break-words text-lg font-bold leading-relaxed text-red-600"
                    data-testid="grading-row-answer"
                  >
                    <GradingCorrectAnswer correctAnswer={row.correctAnswer} questionSnapshot={row.questionSnapshot} />
                  </div>
                ) : (
                  <span className="text-sm text-muted-foreground" data-testid="grading-row-no-answer">
                    {t('gradingMode.noCorrectAnswer')}
                  </span>
                )}
              </dd>
            </dl>
          ) : null}
        </div>
        <div className="flex flex-wrap items-center justify-end gap-2 lg:flex-nowrap">
          <div className="w-full max-w-[260px] lg:w-[220px]">
            <PartialCreditGradeToggle
              value={shown}
              onChange={onToggle}
              disabled={readOnly || disabled}
              allowPartial={row.allowPartial}
              ariaLabel={t('gradingMode.toggleAriaLabel', { label })}
            />
          </div>
          {!readOnly && onRequestCoaching ? (
            <AnswerEditButton onClick={() => onRequestCoaching(row.key)} disabled={disabled} />
          ) : null}
          <SolutionPreviewToggle
            expanded={previewOpen}
            controlsId={previewId}
            onClick={onTogglePreview}
          />
        </div>
      </div>
      {previewOpen ? (
        <div id={previewId}>
          <AdultQuestionView
            key={`grading-preview-${row.key}`}
            mode="preview"
            quizId={row.key}
            order={row.no - 1}
            questionSnapshot={row.questionSnapshot}
            correctAnswer={row.correctAnswer}
            explanation={row.explanation ?? undefined}
            hideSolveDescription={hidePreviewSolveDescription}
          />
        </div>
      ) : null}
    </li>
  )
}
